import { forwardRef, useMemo } from "react";

const SIZE = 1080;

/* Off-screen Instagram-square card rendered to PNG via html-to-image. */
export const ShareCard = forwardRef(function ShareCard({ plan, vegBySlug, families, iconUrl, subtitle }, ref) {
  const beds = plan?.beds || [];

  const cellPx = useMemo(() => {
    if (!beds.length) return 48;
    const maxCols = Math.max(...beds.map((b) => b.cols), 1);
    const sumRows = beds.reduce((a, b) => a + b.rows, 0) + beds.length * 0.9;
    return Math.max(14, Math.min(64, Math.floor(960 / maxCols), Math.floor(700 / sumRows)));
  }, [beds]);

  const stats = useMemo(() => {
    const slugs = new Set();
    const fams = new Set();
    let planted = 0;
    beds.forEach((b) => (b.placements || []).forEach((p) => {
      planted++; slugs.add(p.slug);
      const fk = vegBySlug[p.slug]?.family_key; if (fk) fams.add(fk);
    }));
    return { planted, crops: slugs.size, fams: fams.size };
  }, [beds, vegBySlug]);

  const BED_LABEL = { raised: "Raised Bed", polytunnel: "Polytunnel", open: "Open Ground", container: "Container", tray: "Seed Tray" };

  return (
    <div
      ref={ref}
      style={{
        position: "fixed", top: 0, left: -2000, width: SIZE, height: SIZE,
        background: "linear-gradient(140deg,#1f3d2b 0%,#2f5d3f 45%,#3f7350 100%)",
        fontFamily: "Georgia, 'Times New Roman', serif", color: "#f3f8f1",
        display: "flex", flexDirection: "column", overflow: "hidden",
        boxSizing: "border-box", padding: 64,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, background: "#86efac", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 38 }}>🌱</div>
          <div style={{ fontSize: 34, fontWeight: 700, letterSpacing: 1 }}>Sprout Log</div>
        </div>
        <div style={{ fontSize: 22, opacity: 0.85 }}>{subtitle || "Garden Plan"}</div>
      </div>

      {/* Title */}
      <div style={{ marginTop: 28, marginBottom: 20 }}>
        <div style={{ fontSize: 56, fontWeight: 800, lineHeight: 1.05 }}>{plan?.name || "My Garden"}</div>
        <div style={{ fontSize: 24, opacity: 0.8, marginTop: 8 }}>
          {stats.planted} plants · {stats.crops} crops · {stats.fams} families
        </div>
      </div>

      {/* Beds */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 22, justifyContent: "center" }}>
        {beds.length === 0 && (
          <div style={{ fontSize: 28, opacity: 0.7, textAlign: "center" }}>An empty plot, full of possibility.</div>
        )}
        {beds.map((bed) => {
          const placed = {};
          (bed.placements || []).forEach((p) => { placed[`${p.col}-${p.row}`] = p; });
          return (
            <div key={bed.id} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, opacity: 0.9 }}>
                {bed.name} <span style={{ opacity: 0.6, fontWeight: 400 }}>· {BED_LABEL[bed.type] || "Bed"}</span>
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: `repeat(${bed.cols}, ${cellPx}px)`,
                gridAutoRows: `${cellPx}px`,
                border: "3px solid rgba(255,255,255,0.35)", borderRadius: 12, overflow: "hidden",
                background: "repeating-linear-gradient(45deg,rgba(255,255,255,0.06) 0 8px,rgba(255,255,255,0.11) 8px 16px)",
              }}>
                {Array.from({ length: bed.rows * bed.cols }).map((_, i) => {
                  const c = i % bed.cols; const r = Math.floor(i / bed.cols);
                  const p = placed[`${c}-${r}`];
                  const veg = p ? vegBySlug[p.slug] : null;
                  return (
                    <div key={i} style={{
                      width: cellPx, height: cellPx, borderRight: "1px solid rgba(255,255,255,0.12)", borderBottom: "1px solid rgba(255,255,255,0.12)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: veg ? `${veg.family_color}33` : "transparent",
                    }}>
                      {veg && <img src={iconUrl(veg.slug)} crossOrigin="anonymous" alt="" style={{ width: "84%", height: "84%", objectFit: "contain" }} />}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.25)" }}>
        <div style={{ fontSize: 22, opacity: 0.85 }}>Grown &amp; planned with Sprout Log 🌿</div>
        <div style={{ display: "flex", gap: 10 }}>
          {Object.entries(families || {}).slice(0, 6).map(([k, v]) => (
            <span key={k} style={{ width: 20, height: 20, borderRadius: 999, background: v.color, display: "inline-block" }} />
          ))}
        </div>
      </div>
    </div>
  );
});
